import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "@/lib/router-compat";
import { Helmet } from "react-helmet-async";
import { useThemeLayout } from "@/hooks/useThemeLayout";
import { getProduct, listSuggestions, listProductsByAddonIds, productImages, productTitle, productHeading, productSubheading, productPrice, productAvailability, productShipping, productPath, type CdnProduct } from "@/lib/cdnProducts";
import { Navigate } from "@/lib/router-compat";
import { Button } from "@/components/ui/button";
import AddToCartButton from "@/components/parts/AddToCartButton";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Loader2, Search, ZoomIn } from "lucide-react";
import DOMPurify from "dompurify";
import { supabase } from "@/integrations/supabase/client";
import ProtectedImage from "@/components/parts/ProtectedImage";
import { Warehouse } from "lucide-react";
import { Tag as TagIcon } from "lucide-react";
import PartsCatalogFooter from "@/components/parts/PartsCatalogFooter";
import PartsBreadcrumb from "@/components/parts/PartsBreadcrumb";
import { usePartsDetailTemplate, DEFAULT_PARTS_DETAIL_TEMPLATE } from "@/hooks/usePartsDetailTemplate";
import { useHeroContent } from "@/hooks/useHeroContent";
import HeroButtons from "@/components/ui/HeroButtons";
import { STATIC_ASSETS } from "@/lib/imageUtils";
import ProductReviews, { ReviewSummary } from "@/components/parts/ProductReviews";
import FitmentPulseButton from "@/components/parts/FitmentAI/FitmentPulseButton";
import ProductDocuments from "@/components/parts/ProductDocuments";
import BlueOxBaseplateFitment from "@/components/parts/BlueOxBaseplateFitment";
import PartsLeadForm from "@/components/parts/PartsLeadForm";

const ImageZoomViewer = lazy(() => import("@/components/ui/ImageZoomViewer"));

function SafeHtml({ html, className = "" }: { html?: string; className?: string }) {
  if (!html) return null;
  return (
    <div
      className={`prose prose-sm max-w-none ${className}`}
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
    />
  );
}

function FitmentList({ models }: { models: string[] }) {
  const [expanded, setExpanded] = useState(false);
  const clean = models.map((m) => m.trim()).filter(Boolean);
  if (clean.length === 0) return null;
  const TOP = 5;
  const showScroll = expanded || clean.length <= TOP;
  return (
    <div className="mt-6 rounded-lg border border-border bg-card p-4">
      <h3 className="font-semibold text-base mb-1">Possible Fitment ({clean.length})</h3>
      <p className="text-xs text-muted-foreground mb-3">
        This part may fit the models listed below. Always confirm fitment with the manufacturer before purchasing.
      </p>
      {clean.length > TOP && !expanded ? (
        <>
          <ul className="space-y-1 text-sm">
            {clean.slice(0, TOP).map((m, i) => (
              <li key={i} className="text-foreground">{m}</li>
            ))}
          </ul>
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="mt-3 text-sm font-medium text-primary hover:underline"
          >
            Show all {clean.length} models
          </button>
        </>
      ) : (
        <ul className="space-y-1 text-sm max-h-64 overflow-y-auto pr-2 rounded-sm border border-border/60 bg-background/50 p-2">
          {clean.map((m, i) => (
            <li key={i} className="text-foreground">{m}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function PartsCatalogDetail() {
  const { id } = useParams<{ id: string }>();
  const layoutForOffset = useThemeLayout();
  const navigate = useNavigate();
  const location = useLocation();
  const stateFrom = (location.state as { from?: string } | null)?.from;
  let storedFrom: string | null = null;
  try { storedFrom = sessionStorage.getItem("parts-catalog:lastResults"); } catch {}
  const isResultsPath = (p: string | null | undefined) =>
    !!p && (
      p.startsWith("/parts-catalog/results") ||
      p.startsWith("/parts-catalog/category/") ||
      p.startsWith("/parts-catalog/manufacturer/")
    );
  const resultsFrom =
    (isResultsPath(stateFrom) && stateFrom) ||
    (isResultsPath(storedFrom) && storedFrom) ||
    null;
  const backTo = resultsFrom || "/parts-catalog";
  const backLabelFinal = resultsFrom ? "Back to results" : undefined;
  const [product, setProduct] = useState<CdnProduct | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeImg, setActiveImg] = useState(0);
  const [zoomIdx, setZoomIdx] = useState<number | null>(null);
  const [related, setRelated] = useState<CdnProduct[]>([]);
  const [tagMap, setTagMap] = useState<{ tag_slug: string | null; tag_name: string | null } | null>(null);
  const [reviewSummary, setReviewSummary] = useState<{ average: number; count: number }>({ average: 0, count: 0 });
  const { data: template = DEFAULT_PARTS_DETAIL_TEMPLATE } = usePartsDetailTemplate();
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<CdnProduct[]>([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const suggestBoxRef = useRef<HTMLFormElement>(null);
  const { data: cmsHero } = useHeroContent("parts-catalog", "hero_slides");
  const cmsSlide = cmsHero?.slides?.[0];
  const heroTitle = cmsSlide?.title || "RV Parts Catalog";
  const heroSubtitle = cmsSlide?.subtitle;
  const heroDesktop = cmsSlide?.image_desktop;
  const heroMobile = cmsSlide?.image_mobile;
  const heroButtons = cmsSlide?.buttons;

  useEffect(() => {
    const q = query.trim();
    if (q.length < 3) {
      setSuggestions([]);
      setSuggestLoading(false);
      return;
    }
    setSuggestLoading(true);
    const t = setTimeout(() => {
      listSuggestions(q, 3)
        .then((items) => { setSuggestions(items); setActiveIdx(-1); })
        .catch(() => setSuggestions([]))
        .finally(() => setSuggestLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  function submitSearch(e: React.FormEvent) {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    setShowSuggest(false);
    navigate(`/parts-catalog/results?search=${encodeURIComponent(q)}`);
  }

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setProduct(null);
    setError(null);
    setActiveImg(0);
    getProduct(id)
      .then((res) => {
        if (cancelled) return;
        const p = (res as any)?.product ?? (res as any)?.data ?? res;
        setProduct(p);
      })
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    const ids = product?.related_addon_ids;
    if (!ids || ids.length === 0) { setRelated([]); return; }
    let cancelled = false;
    listProductsByAddonIds(ids, 12)
      .then((items) => { if (!cancelled) setRelated(items); })
      .catch(() => { if (!cancelled) setRelated([]); });
    return () => { cancelled = true; };
  }, [product?.ProductID, product?.related_addon_ids]);

  // Resolve the canonical (cleaned) tag slug for this product's legacy name so
  // the tag chip never links to an empty /producttag/ page.
  const legacyName = (product as any)?.generic_legacy_name as string | null | undefined;
  useEffect(() => {
    if (!legacyName) { setTagMap(null); return; }
    let cancelled = false;
    supabase
      .from("generic_legacy_tag_map")
      .select("tag_slug, tag_name")
      .eq("legacy_name", legacyName)
      .maybeSingle()
      .then(({ data }) => { if (!cancelled) setTagMap(data ?? null); });
    return () => { cancelled = true; };
  }, [legacyName]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-16 flex justify-center text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading...
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="container mx-auto px-4 py-16">
        <p className="text-destructive">{error || "Product not found."}</p>
        <Link to={backTo}><Button variant="outline" className="mt-4"><ArrowLeft className="w-4 h-4 mr-2" />{backLabelFinal || "Back"}</Button></Link>
      </div>
    );
  }

  const title = productTitle(product);
  const heading = productHeading(product);
  const subheading = productSubheading(product);
  const images = productImages(product);
  const price = productPrice(product);
  const stripHtml = (s: string) => s.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  const hasAuthoredMetaTitle = !!(product.MetaTitle as string);
  const metaTitle = ((product.MetaTitle as string) || title).slice(0, 70);
  const metaKeywords = (product.MetaKeywords as string | undefined) || "";
  const metaDesc = (
    (product.MetaDescription as string) ||
    stripHtml((product.MarketingDescription as string) || (product.Description as string) || "") ||
    title
  ).slice(0, 160);
  const path = productPath(product);
  const canonical = `https://www.rexandsonsrvs.com${path}`;
  // If the URL was a ProductID but the row has a slug, redirect to the SEO URL.
  // If the URL is the legacy long slug (or a bare ProductID) but a short slug
  // now exists, redirect to the canonical short URL so search engines settle
  // on the new path.
  const onShortSlug = !!product.short_slug && id === product.short_slug;
  const hasBetterUrl = !!product.short_slug && !onShortSlug;
  // Guard against stale-product redirects: if the URL `id` was changed (e.g. a
  // Related Products click) but the previous product is still in state while
  // the new fetch is in flight, skip the canonical redirect — otherwise we'd
  // bounce the user back to the original product.
  const idMatchesLoaded =
    !!id && (
      id === (product.short_slug as string | undefined) ||
      id === (product.admin_slug as string | undefined) ||
      id === (product.Slug as string | undefined) ||
      id === String(product.ProductID) ||
      id === (product['product_number'] as string | undefined) ||
      // Legacy slug (canonical short slug minus the leading vendor-code token)
      ((product.short_slug as string | undefined)?.endsWith(`-${id}`) ?? false)
    );
  if (!idMatchesLoaded) {
    return (
      <div className="container mx-auto px-4 py-16 flex justify-center text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading...
      </div>
    );
  }
  if (hasBetterUrl) {
    return <Navigate to={path} replace />;
  }
  const looksLikeId = !!id && (/^\d+$/.test(id) || id === String(product.ProductID));
  if (looksLikeId && (product.Slug || product.admin_slug)) {
    return <Navigate to={path} replace />;
  }
  const ogImage = images[0];
  const availability = productAvailability(product);
  // The stored total is authoritative: 0 means out of stock, whatever the
  // warehouse columns or availability text say.
  const inStock = availability.total > 0;
  const jsonLd: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: title,
    description: metaDesc,
    sku: String(product.ProductID),
    url: canonical,
  };
  if (ogImage) jsonLd['image'] = images.slice(0, 6);
  if (product.VendorName) jsonLd['brand'] = { "@type": "Brand", name: product.VendorName };
  if (reviewSummary.count > 0) {
    jsonLd['aggregateRating'] = {
      "@type": "AggregateRating",
      ratingValue: reviewSummary.average.toFixed(2),
      reviewCount: reviewSummary.count,
      bestRating: 5,
      worstRating: 1,
    };
  }
  const reviewSlug =
    (product.admin_slug as string | undefined) ||
    (product.Slug as string | undefined) ||
    (product.short_slug as string | undefined) ||
    String(product.ProductID);
  if (product.PartTypeName) jsonLd['category'] = product.PartTypeName;
  if (price != null) {
    jsonLd['offers'] = {
      "@type": "Offer",
      url: canonical,
      priceCurrency: "USD",
      price: Number(price).toFixed(2),
      availability: inStock
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock",
      seller: { "@type": "Organization", name: "Rex & Sons RVs" },
      hasMerchantReturnPolicy: {
        "@type": "MerchantReturnPolicy",
        applicableCountry: "US",
        returnPolicyCategory: "https://schema.org/MerchantReturnNotPermitted",
      },
      shippingDetails: {
        "@type": "OfferShippingDetails",
        shippingDestination: { "@type": "DefinedRegion", addressCountry: "US" },
        shippingRate: { "@type": "MonetaryAmount", value: 0, currency: "USD" },
        deliveryTime: {
          "@type": "ShippingDeliveryTime",
          handlingTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 1, unitCode: "DAY" },
          transitTime: { "@type": "QuantitativeValue", minValue: 1, maxValue: 5, unitCode: "DAY" },
        },
      },
    };
  }

  const dims: string[] = [];
  if (product['Weight']) dims.push(`Weight: ${product['Weight']}`);
  if (product['Length']) dims.push(`Length: ${product['Length']}`);
  if (product['Width']) dims.push(`Width: ${product['Width']}`);
  if (product['Height']) dims.push(`Height: ${product['Height']}`);

  const tokens: Record<string, string> = {
    "{id}": String(product.ProductID ?? ""),
    "{slug}": (product.admin_slug as string) || (product.Slug as string) || "",
    "{title}": encodeURIComponent(title),
  };
  const ctaUrl = template.primaryCtaUrl.replace(/\{id\}|\{slug\}|\{title\}/g, (m) => tokens[m] ?? "");

  const themeLayout = layoutForOffset;
  return (
    <div>
      <Helmet>
        <title>{hasAuthoredMetaTitle ? metaTitle : `${metaTitle} | Rex & Sons RVs`}</title>
        <meta name="description" content={metaDesc} />
        {metaKeywords && <meta name="keywords" content={metaKeywords} />}
        {ogImage && <meta property="og:image" content={ogImage} />}
        {ogImage && <meta name="twitter:image" content={ogImage} />}
        {price != null && <meta property="product:price:amount" content={Number(price).toFixed(2)} />}
        {price != null && <meta property="product:price:currency" content="USD" />}
        <meta property="product:availability" content={inStock ? "in stock" : "out of stock"} />
        {product.VendorName && <meta property="product:brand" content={product.VendorName as string} />}
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <section className={`relative z-[100] flex min-h-[320px] md:min-h-[400px] items-center justify-center overflow-visible bg-secondary ${themeLayout === "carmax" ? "" : "-mt-[140px] pt-[140px]"}`}>
        <picture className="absolute inset-0 h-full w-full">
          <source srcSet={heroMobile || STATIC_ASSETS.heroService640} media="(max-width: 768px)" type="image/webp" />
          <source srcSet={heroDesktop || STATIC_ASSETS.heroService1920} media="(min-width: 769px)" type="image/webp" />
          <img
            src={heroDesktop || STATIC_ASSETS.heroService1920}
            alt=""
            className="h-full w-full object-cover"
            loading="eager"
            decoding="sync"
          />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-b from-secondary/90 via-secondary/70 to-secondary/90" />
        <div className="relative z-10 container py-10 text-center">
          <h1 className="font-heading text-3xl font-bold uppercase tracking-wide text-primary-foreground md:text-5xl">
            {heroTitle}
          </h1>
          {heroSubtitle && (
            <p className="mx-auto mt-3 max-w-2xl text-base md:text-lg text-secondary-foreground/80">
              {heroSubtitle}
            </p>
          )}
          <form onSubmit={submitSearch} className="mx-auto mt-6 flex max-w-2xl gap-2 relative z-[1000]" ref={suggestBoxRef}>
            <div className="relative flex-1">
              <Input
                value={query}
                onChange={(e) => { setQuery(e.target.value); setShowSuggest(true); }}
                onFocus={() => setShowSuggest(true)}
                placeholder="Search parts..."
                autoComplete="off"
                className="h-12 bg-background/95 text-base shadow-lg"
              />
              {showSuggest && query.trim().length >= 3 && (
                <div className="absolute z-[1000] left-0 right-0 top-full mt-1 bg-popover border border-border rounded-md shadow-lg overflow-hidden text-left">
                  {suggestLoading && suggestions.length === 0 ? (
                    <div className="px-3 py-3 text-sm text-muted-foreground flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Searching…
                    </div>
                  ) : suggestions.length === 0 ? (
                    <div className="px-3 py-3 text-sm text-muted-foreground">No matches</div>
                  ) : (
                    <ul className="max-h-96 overflow-auto">
                      {suggestions.map((p) => {
                        const sid = String(p.ProductID);
                        const stitle = productTitle(p);
                        const simg = productImages(p)[0];
                        const sprice = productPrice(p);
                        return (
                          <li key={sid}>
                            <Link
                              to={productPath(p)}
                              state={(resultsFrom ? { from: resultsFrom } : undefined) as any}
                              onClick={() => setShowSuggest(false)}
                              className="flex items-center gap-3 px-3 py-2 text-sm hover:bg-muted"
                            >
                              <div className="w-10 h-10 flex-shrink-0 bg-muted rounded-sm flex items-center justify-center overflow-hidden">
                                {simg ? (
                                  <ProtectedImage src={simg} alt={stitle} className="w-full h-full" objectFit="contain" loading="lazy" />
                                ) : (
                                  <span className="text-[10px] text-muted-foreground">No img</span>
                                )}
                              </div>
                              <span className="flex-1 line-clamp-1">{stitle}</span>
                              {sprice != null && (
                                <span className="text-primary font-semibold whitespace-nowrap">${sprice.toFixed(2)}</span>
                              )}
                            </Link>
                          </li>
                        );
                      })}
                      <li className="border-t border-border">
                        <button type="submit" className="w-full text-left px-3 py-2 text-sm text-primary hover:bg-muted font-medium">
                          See all results for "{query.trim()}"
                        </button>
                      </li>
                    </ul>
                  )}
                </div>
              )}
            </div>
            <Button type="submit" size="lg" className="h-12">
              <Search className="w-4 h-4 mr-2" />Search
            </Button>
          </form>
          {heroButtons && heroButtons.length > 0 && (
            <div className="mt-6">
              <HeroButtons buttons={heroButtons} />
            </div>
          )}
        </div>
      </section>

      <div className="container mx-auto px-4 py-8">
        <PartsBreadcrumb crumbs={[
          { label: "Parts Catalog", to: "/parts-catalog" },
          { label: heading },
        ]} />
      {template.showBreadcrumb && (
        <Link to={backTo}>
          <Button variant="ghost" size="sm" className="mb-4"><ArrowLeft className="w-4 h-4 mr-2" />{backLabelFinal || template.backLinkLabel}</Button>
        </Link>
      )}

      {template.headerHtml && (
        <div className="mb-6">
          <SafeHtml html={template.headerHtml} />
        </div>
      )}

      {/* Top section */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold">{heading}</h1>
        {subheading && (
          <p className="mt-1 text-sm md:text-base text-muted-foreground leading-snug">
            {subheading}
          </p>
        )}
        {price != null && (
          <div className="text-3xl font-bold text-primary mt-2">${price.toFixed(2)}</div>
        )}
        {reviewSummary.count > 0 && (
          <div className="mt-2">
            <ReviewSummary
              average={reviewSummary.average}
              count={reviewSummary.count}
              onClick={() => {
                document.getElementById("reviews")?.scrollIntoView({ behavior: "smooth" });
              }}
            />
          </div>
        )}
        {(template.showVendorMeta || template.showDimensions) && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-muted-foreground">
            {template.showVendorMeta && product.VendorName && <span>{product.VendorName}</span>}
            {template.showVendorMeta && product.PartTypeName && <span>{product.PartTypeName}</span>}
            {template.showDimensions && dims.map((d) => (
              <span key={d}>{d}</span>
            ))}
          </div>
        )}
        {(() => {
          const tag = (product as any).generic_legacy_name as string | null | undefined;
          if (!tag) return null;
          // Canonical tag slugs are cleaned server-side (brands/short tokens
          // stripped), so "LIFT KIT" lives at /producttag/lift. Prefer the
          // mapped slug; without it the tag page would be empty.
          const mapped = tagMap;
          const rawSlug = tag.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
          const slug = mapped?.tag_slug || rawSlug;
          if (!slug || (mapped && !mapped.tag_slug)) return null;
          const label = (mapped?.tag_name || tag)
            .toLowerCase()
            .split(/\s+/)
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(" ");
          return (
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <Link
                to={`/producttag/${slug}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/40 px-3 py-1 text-xs font-medium text-muted-foreground hover:border-primary/40 hover:text-primary transition-colors"
              >
                <TagIcon className="h-3 w-3" /> {label}
              </Link>
            </div>
          );
        })()}
        {price != null && (
          <div className="mt-4">
            <AddToCartButton
              productId={String(product.ProductID)}
              disabled={!inStock}
              {...(typeof product.InventoryTotal === "number" ? { maxQty: product.InventoryTotal } : {})}
              showQty
            />
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Left: Image carousel */}
        <div>
          <div className="relative aspect-[4/3] bg-muted rounded-lg overflow-hidden mb-3 flex items-center justify-center parts-no-save">
            {images[activeImg] ? (
              <>
                <Button variant="ghost" aria-label="Zoom in on part photo" title="Zoom photo" onClick={() => setZoomIdx(activeImg)} className="h-full w-full p-0 cursor-zoom-in hover:bg-transparent"><ProtectedImage src={images[activeImg]} alt={title} className="w-full h-full" objectFit="contain" loading="eager" /></Button>
                <Button variant="secondary" size="icon" aria-label="Enlarge part photo" title="Enlarge part photo" onClick={() => setZoomIdx(activeImg)} className="absolute top-2 right-2"><ZoomIn className="h-5 w-5" /></Button>
              </>
            ) : (
              <span className="text-muted-foreground">No image</span>
            )}
          </div>
          {zoomIdx !== null && <Suspense fallback={null}><ImageZoomViewer photos={images.map(src => ({ src }))} index={zoomIdx} onIndexChange={setZoomIdx} title={title} protectedImage /></Suspense>}
          {images.length > 1 && (
            <div className="flex gap-2 flex-wrap">
              {images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`w-16 h-16 rounded-sm border-2 overflow-hidden parts-no-save ${i === activeImg ? "border-primary" : "border-transparent"}`}
                >
                  <ProtectedImage src={img} alt={`${title} ${i + 1}`} className="w-full h-full" objectFit="cover" loading="lazy" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Right: Description + optional MarketingDescription */}
        <div>
          {template.showWarehouseAvailability && inStock && availability.regions.length > 0 && (
            <div className="mb-4 rounded-lg border bg-card p-4">
              <div className="flex items-start gap-3">
                <Warehouse className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <div className="text-sm w-full">
                  <div className="font-semibold mb-2">
                    In stock at {availability.regions.length} warehouse{availability.regions.length === 1 ? "" : "s"}
                    {availability.total > 0 && (
                      <span className="text-muted-foreground font-normal"> · {availability.total} available</span>
                    )}
                  </div>
                  <ul className="space-y-1">
                    {availability.regions.map((r) => (
                      <li key={r.name} className="flex justify-between text-muted-foreground">
                        <span>{r.name}</span>
                        <span className="font-medium text-foreground">{r.qty}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {product.DescriptionHtml ? (
            <SafeHtml html={product.DescriptionHtml as string} />
          ) : product.Description ? (
            <SafeHtml html={product.Description as string} />
          ) : null}
          {template.showMarketingDescription && !product.DescriptionHtml && product.MarketingDescription && product.MarketingDescription !== product.Description && (
            <div className="mt-6">
              <SafeHtml
                html={product.MarketingDescription as string}
                className="text-sm text-muted-foreground"
              />
            </div>
          )}
          {Array.isArray(product.ApplicationModels) && product.ApplicationModels.length > 0 && (
            <FitmentList models={product.ApplicationModels as string[]} />
          )}
          <FitmentPulseButton product={product} />

          <PartsLeadForm
            productId={product.ProductID}
            partNumber={(product["product_number"] as string | null) ?? null}
            partName={title}
            partSku={String(product.ProductID)}
            price={price}
            pageUrl={canonical}
            pageTitle={heading}
            className="mt-8"
          />
        </div>
      </div>

      <ProductDocuments product={product} />

      <BlueOxBaseplateFitment partNumber={(product["product_number"] as string | null) ?? null} />

      {(product.all_features_html || product.all_attributes_html) && (
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-8">
          {product.all_features_html && (
            <section>
              <h2 className="text-xl font-bold mb-3">Features</h2>
              <SafeHtml html={product.all_features_html as string} />
            </section>
          )}
          {product.all_attributes_html && (
            <section>
              <h2 className="text-xl font-bold mb-3">Specifications</h2>
              <SafeHtml html={product.all_attributes_html as string} />
            </section>
          )}
        </div>
      )}

      {related.length > 0 && (
        <section className="mt-12">
          <h2 className="text-2xl font-bold mb-4">Related Products</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {related.map((rp) => {
              const rid = String(rp.ProductID);
              const rtitle = productTitle(rp);
              const rimg = productImages(rp)[0];
              const rprice = productPrice(rp);
              return (
                <Link
                  key={rid}
                  to={productPath(rp)}
                  state={(resultsFrom ? { from: resultsFrom } : undefined) as any}
                  className="group rounded-lg border border-border bg-card overflow-hidden hover:shadow-md hover:border-primary/40 transition-all flex flex-col"
                >
                  <div className="aspect-square bg-muted flex items-center justify-center overflow-hidden parts-no-save">
                    {rimg ? (
                      <ProtectedImage src={rimg} alt={rtitle} className="w-full h-full" objectFit="contain" loading="lazy" />
                    ) : (
                      <span className="text-xs text-muted-foreground">No image</span>
                    )}
                  </div>
                  <div className="p-3 flex flex-col flex-1">
                    {rp.VendorName && (
                      <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1 line-clamp-1">
                        {rp.VendorName as string}
                      </div>
                    )}
                    <div className="text-sm font-medium line-clamp-2 group-hover:text-primary">{rtitle}</div>
                    {rprice != null && (
                      <div className="text-primary font-bold mt-2">${rprice.toFixed(2)}</div>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      <ProductReviews
        productSlug={reviewSlug}
        productTitle={title}
        onSummaryChange={setReviewSummary}
      />

      {template.footerHtml && (
        <div className="mt-10">
          <SafeHtml html={template.footerHtml} />
        </div>
      )}
      </div>
      <PartsCatalogFooter />
    </div>
  );
}